import { memo, useCallback, useMemo, useState } from 'react';
import TableBody from '@mui/material/TableBody';
import Table from '@mui/material/Table';
import Paper from '@mui/material/Paper';
import TableContainer from 'src/components/Table/TableContainer';
import TableHeading from 'src/components/Table/TableHeading';
import LoadingRows from 'src/components/Table/LoadingRows';
import Row from 'src/components/Table/Row';
import NoDataCell from 'src/components/NoDataCell';
import { catalogInventoryTableColumnsOrder, catalogInventoryTableHeadings } from './constants';
import { Box } from '@mui/material';
import TableSearch from 'src/components/elements/TableSearch';
import UnsignedCatalogsInfo from './UnsignedCatalogsInfo';
import useQueryParams from 'src/hooks/useQueryParams';
import useDebounceHandler from 'src/hooks/useDebounceHandler';
import {
  defaultSearchParams,
  reversedSortMap,
  ROW_SOURCES,
  sortingMethods,
} from 'src/constants/table';
import useResetSearchParams from 'src/hooks/useResetSearchParams';
import FilterCount from 'src/components/Filters/FilterCount';
import { checkHasFilters } from 'src/utils/table.util';
import GenericTableCell from 'src/components/GenericTableCell';
import { isDateString } from 'src/utils/date.util';
import { formatDate } from 'src/utils/formatTime';
import useTableSort from '../../../../hooks/useTableSort';
import { defaultCatalogInventoryFilters } from '../../../../components/Filters/constants';
import WdacCatalogInventorySidebar from './WdacCatalogInventorySidebar';
import { colors } from 'src/theme/palette/darkPalette';

const WdacCatalogInventoryTable = ({
  isLoading,
  catalogInventoryList,
  lastElementRef,
  isFetching,
  totalCount,
  unsignedCount,
  query,
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [isOpenFilters, setIsOpenFilters] = useState(false);
  useResetSearchParams();

  const {
    queryObject: { page, rowsPerPage, filters = {} },
    changeFieldInURL,
    onApplySearchParams,
  } = useQueryParams();

  const { onSort, onSortTable, onResetSortField, getListItemSortMethod, getCellSortMethod } =
    useTableSort({
      filters,
      onApplySearchParams,
      page,
      rowsPerPage,
      searchValue,
      defaultFilters: defaultCatalogInventoryFilters,
    });

  const onCellSort = useCallback(
    ({ field, sortMethod, sortingCellField, sortFieldName }) => {
      const reversedSortOrder =
        sortMethod === sortingMethods.initial ? sortingMethods.desc : reversedSortMap[sortMethod];

      onSort(field, reversedSortOrder, sortingCellField, sortFieldName);
    },
    [onSort],
  );

  const onApplyFilters = useCallback(
    filters => {
      onApplySearchParams({ filters, page: 1, limit: rowsPerPage, searchValue });
    },
    [onApplySearchParams, rowsPerPage, searchValue],
  );

  const onCloseFilters = useCallback(() => {
    setIsOpenFilters(false);
  }, [setIsOpenFilters]);

  const onOpenFilters = useCallback(() => {
    setIsOpenFilters(true);
  }, [setIsOpenFilters]);

  const debounceQueryHandler = useDebounceHandler((key, value) => {
    changeFieldInURL([{ key, value }]);
  }, 500);

  const onChangeSearchValue = useCallback(
    event => {
      setSearchValue(event.target.value);
      debounceQueryHandler('search', event.target.value.trim());
    },
    [debounceQueryHandler],
  );

  const onResetSearch = useCallback(() => {
    setSearchValue('');
    changeFieldInURL([defaultSearchParams]);
  }, [setSearchValue, changeFieldInURL]);

  const hasFilters = useMemo(() => checkHasFilters(filters), [filters]);

  const filteredCount = useMemo(() => {
    return !isLoading && (hasFilters || searchValue) ? totalCount : null;
  }, [totalCount, hasFilters, isLoading, searchValue]);

  const renderCustomCell = useCallback((column, row) => {
    let cellValue = row[column?.name || column];
    
    // Format origin field to add space
    if ((column?.name || column) === 'origin' && cellValue) {
      cellValue = cellValue.replace(/([a-z])([A-Z])/g, '$1 $2');
    }
    
    return (
      <GenericTableCell
        key={column?.name || column}
        isNumber={column?.isNumber}
        value={
          column?.shouldHideValue
            ? ''
            : isDateString(cellValue)
              ? formatDate(cellValue)
              : cellValue
        }
        styles={column?.styles || {}}
        {...column?.props}
      />
    );
  }, []);

  return (
    <Box sx={{ position: 'relative' }}>
      <UnsignedCatalogsInfo unsignedCount={unsignedCount}/>

      <TableSearch
        onResetSearch={onResetSearch}
        onChangeSearchValue={onChangeSearchValue}
        searchValue={searchValue}
      />

      <FilterCount filteredCount={filteredCount} onOpenFilters={onOpenFilters} />

      <Paper
        elevation={0}
        sx={{
          display: 'flex',
          flexDirection: 'column',
        }}>
        <TableContainer
          square
          component={Paper}
          elevation={0}
          styles={{
            maxHeight: 'calc(100vh - 354px)',
          }}>
          <Table stickyHeader aria-label="collapsible table">
            <TableHeading
              headings={catalogInventoryTableHeadings}
              listCellStyles={{
                top: '0px',
                padding: '10px 16px',
              }}
              onSort={onSort}
              filterTopPisition="0"
              onSortTable={onSortTable}
              onResetSortField={onResetSortField}
              getListItemSortMethod={getListItemSortMethod}
              getCellSortMethod={getCellSortMethod}
              onCellSort={onCellSort}
            />

            <TableBody sx={{ backgroundColor: '#4B4B4B' }}>
              {isLoading ? (
                <LoadingRows columnCount={catalogInventoryTableHeadings.length} rowCount={3} />
              ) : (
                <>
                  {catalogInventoryList?.map((row, index) => {
                    const isUniqueRow = !row.isSigned;

                    return (
                      <Row
                        withTwoColors={true}
                        hasActions={false}
                        ref={index === catalogInventoryList.length - 1 ? lastElementRef : null}
                        key={row?.id}
                        row={row}
                        columnData={catalogInventoryTableColumnsOrder}
                        isClickable={true}
                        source={ROW_SOURCES.catalogInventory}
                        isUniqueRow={isUniqueRow}
                        renderCustomCell={renderCustomCell}
                        backgroundColor={colors.red}
                      />
                    );
                  })}
                  {!isLoading && isFetching && (
                    <LoadingRows columnCount={catalogInventoryTableHeadings.length} rowCount={3} />
                  )}
                  {!isLoading && !isFetching && !catalogInventoryList?.length && (
                    <NoDataCell colSpan={catalogInventoryTableHeadings.length}>No Data</NoDataCell>
                  )}
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <WdacCatalogInventorySidebar
          query={query}
          open={isOpenFilters}
          onClose={onCloseFilters}
          onApplyFilters={onApplyFilters}
          initialQueryFilters={filters}
        />
      </Paper>
    </Box>
  );
};

export default memo(WdacCatalogInventoryTable);
